import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { Modal } from '@/modules/common/components/Modal';
import { MoneyInput } from '@/modules/common/components/MoneyInput';
import {
  integerToMoney,
  moneyToInteger,
} from '@/modules/common/lib/moneyHandler';

import { useGetTeams } from '@/modules/employees/hooks/useTeams';
import { IAmount, ITeamOverrideWithTeam } from '@/types/learning-budget';
import { ITeam } from '@/types/team';
import { IUser } from '@/types/user';
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckCircle, InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ModalProps,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import pluralize from 'pluralize';
import { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

export type FormValues = {
  teams: (Pick<ITeam, '_id' | 'name'> & {
    members: string[];
    selected: boolean;
  })[];
  budget: IAmount;
};

export type TeamOutput = ITeamOverrideWithTeam['team'];

export type TeamSubmissionValues = {
  teams: TeamOutput[];
  budget: FormValues['budget'];
};

type LearningBudgetSetTeamsModalProps = Omit<
  ModalProps,
  'onClose' | 'title' | 'subTitle' | 'children'
> & {
  open: boolean;
  onClose: (data?: TeamSubmissionValues) => void;
};

export function LearningBudgetSetTeamsModal({
  onClose,
  ...props
}: LearningBudgetSetTeamsModalProps) {
  const userContext = useCurrentUserContext();
  const employerId = userContext?.employer?._id;

  const [search, setSearch] = useState('');

  const {
    data: { data: teams = [] } = {},
    isLoading: isLoadingTeams,
    isRefetching: isRefetchingTeams,
  } = useGetTeams(
    {
      name: search,
      limit: 10,
    },
    { enabled: !!employerId },
  );

  const isLoading = isLoadingTeams || isRefetchingTeams;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: {
      teams: [],
      budget: {
        amount: 0,
        currency: 'EUR',
      },
    },
    resolver: yupResolver(
      yup.object().shape({
        teams: yup
          .array()
          .of(
            yup
              .object()
              .shape({
                _id: yup.string().required(),
                name: yup.string().required(),
                members: yup
                  .array()
                  .of(yup.string().uuid().required())
                  .required(),
                selected: yup.boolean().required(),
              })
              .required(),
          )
          .required()
          .min(1, 'At least one team is required')
          .test(
            'teams-selected',
            'At least one team should be selected',
            value => value.some(team => team.selected),
          ),
        budget: yup.object().shape({
          amount: yup
            .number()
            .required('Amount is required')
            .typeError('Amount is required')
            .positive('Amount must be positive'),
          currency: yup.string().required().oneOf(['EUR']),
        }),
      }),
    ),
  });

  const {
    fields: teamsList,
    append,
    update,
  } = useFieldArray({
    control,
    name: 'teams',
  });

  const onSubmitForm = (data: FormValues) => {
    const selectedTeams = data.teams
      .filter(team => team.selected)
      .map(team => ({
        _id: team._id,
        name: team.name,
      }));

    onClose?.({
      teams: selectedTeams,
      budget: data.budget,
    });

    reset();
  };

  return (
    <Modal
      {...props}
      onClose={() => {
        onClose();
        reset();
      }}
      title="Set Learning Budget for Teams"
      subTitle="Allocate learning budgets per team for this cycle."
      actions={
        <>
          <Button
            variant="outlined"
            onClick={() => onClose()}
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="learning-budget-set-teams-modal-form"
            disabled={isLoading}
            loading={isSubmitting}
          >
            Confirm
          </Button>
        </>
      }
      classes={{ paper: 'max-w-2xl' }}
    >
      <form
        id="learning-budget-set-teams-modal-form"
        onSubmit={handleSubmit(onSubmitForm)}
      >
        <Stack gap={3}>
          <Autocomplete<ITeam>
            options={teams}
            getOptionLabel={option => option.name}
            loading={isLoading}
            inputValue={search}
            onInputChange={(_, value) => setSearch(value)}
            handleHomeEndKeys
            selectOnFocus
            clearOnBlur
            onChange={(_, value) => {
              if (
                value?._id &&
                !teamsList.some(team => team._id === value._id)
              ) {
                append({
                  ...value,
                  members: value.members.map(member => (member as IUser)._id),
                  selected: true,
                });
              }
            }}
            renderInput={params => (
              <TextField {...params} size="small" className="my-[2px]" />
            )}
            slotProps={{
              popper: {
                className: 'shadow-md border-none',
              },
              listbox: {
                className: 'py-0',
              },
            }}
            renderValue={value => {
              return (
                <Typography className="mx-2">
                  <span className="font-medium">{value.name}</span>
                </Typography>
              );
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li
                  {...optionProps}
                  key={key}
                  className={`flex flex-col px-3 py-2 items-start cursor-pointer ${
                    optionProps.className ?? ''
                  }`}
                >
                  <span className="font-semibold">{option.name}</span>
                </li>
              );
            }}
          />
          <FormControl className="flex flex-col gap-1" error={!!errors.teams}>
            <List
              className={`rounded-xl border p-0 min-h-12 overflow-auto max-h-[250px] ${
                errors.teams ? 'border-error' : ''
              }`}
            >
              {teamsList.length > 0 ? (
                teamsList.map((team, idx) => (
                  <ListItem
                    key={team._id}
                    divider={idx !== teamsList.length - 1}
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        checked={team.selected}
                        tabIndex={-1}
                        disableRipple
                        icon={
                          <CheckCircle className="text-primary opacity-20" />
                        }
                        checkedIcon={<CheckCircle className="text-primary" />}
                        onChange={() => {
                          update(idx, {
                            ...team,
                            selected: !team.selected,
                          });
                        }}
                      />
                    }
                    className={`py-2 px-4 ${team.selected ? 'bg-light-gray' : ''}`}
                  >
                    <ListItemText
                      className="m-0"
                      primary={
                        <Typography fontWeight={600}>{team.name}</Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {pluralize('member', team.members?.length ?? 0, true)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText>
                    <span className="text-secondary">No teams selected</span>
                  </ListItemText>
                </ListItem>
              )}
            </List>
            <FormHelperText error={!!errors.teams}>
              {errors.teams?.message}
            </FormHelperText>
          </FormControl>

          <FormControl className="flex flex-col gap-1">
            <Typography
              variant="subtitle2"
              component="label"
              htmlFor="budget.amount"
            >
              Enter Learning Budget Amount
            </Typography>

            <Controller
              control={control}
              name="budget.amount"
              render={({
                field: { value, onChange, ref: inputRef, ...field },
              }) => {
                return (
                  <MoneyInput
                    error={!!errors.budget?.amount}
                    value={integerToMoney(value)}
                    onChange={e => {
                      onChange(moneyToInteger(e.target.value));
                    }}
                    {...field}
                    inputRef={inputRef}
                    id="budget.amount"
                    decimalScale={2}
                    startAdornment={
                      <Typography
                        className={`text-2xl font-semibold ${
                          isLoading || isSubmitting ? 'text-gray-400' : ''
                        }`}
                      >
                        €
                      </Typography>
                    }
                    allowNegative={true}
                    classes={{
                      root: 'flex-1 rounded-none text-center',
                      input: 'text-center text-2xl font-semibold',
                      focused: 'outline-none',
                    }}
                  />
                );
              }}
            />
            {errors.budget?.amount && (
              <FormHelperText error={!!errors.budget?.amount}>
                {errors.budget?.amount?.message}
              </FormHelperText>
            )}
          </FormControl>

          <Alert
            severity="info"
            variant="filled"
            className="bg-info-light text-info-dark rounded-lg"
            icon={<InfoOutlined />}
          >
            To add teams, go to the Teams page or click the 'Manage Teams'
            button.
          </Alert>
        </Stack>
      </form>
    </Modal>
  );
}
